import { AxioBoleteria } from "./index";
import { clienteInfo } from "./DatosUsuarioLocalStorag";

/* Notificaciones push de la pagina publica. El permiso se pide (con un aviso propio y luego el del navegador) cuando el cliente
   abre un concierto o compra; nunca al entrar. Todo es opcional: si el navegador no lo soporta, el servidor no lo tiene habilitado o
   el cliente dice que no, la pagina funciona igual. Nunca en el panel (sesion de admin / vendedor). */

const CLAVE_DESCARTE = "push_descartado";
const RUTA_SW = "/sw.js";

export const pushSoportado = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const enElPanel = () => {
    try {
        const ruta = window.location.pathname || "";
        return clienteInfo() != null || ruta.startsWith("/admin") || ruta.startsWith("/auth");
    } catch (_e) { return false; }
};

// permitirPanel: solo para la prueba del admin (Ajustes > Notificaciones push), que se hace desde el panel
export const puedeOfrecerPush = ({ permitirPanel = false } = {}) => pushSoportado() && (permitirPanel || !enElPanel()) && Notification.permission !== "denied";

// El cliente dijo "Ahora no": se le vuelve a ofrecer, cada vez con mas espera para no molestar: 2 dias despues de la 1.a vez,
// 7 despues de la 2.a, 30 despues de la 3.a y, tras la 4.a negativa, no se le pregunta mas. Se pregunta como maximo una vez por visita
// (el aviso solo sale al abrir un concierto o al comprar, no en cada pagina).
const ESPERA_DIAS = [2, 7, 30];
const leerDescarte = () => {
    try {
        const crudo = localStorage.getItem(CLAVE_DESCARTE);
        if (!crudo) return { n: 0, t: 0 };
        if (/^\d+$/.test(crudo)) return { n: 1, t: Number(crudo) }; // formato anterior: solo la fecha
        const d = JSON.parse(crudo);
        return { n: Number(d.n) || 0, t: Number(d.t) || 0 };
    } catch (_e) { return { n: 0, t: 0 }; }
};
export const descartadoReciente = () => {
    const { n, t } = leerDescarte();
    if (n <= 0) return false;
    if (n > ESPERA_DIAS.length) return true; // ya dijo que no varias veces: no se insiste
    return Date.now() - t < ESPERA_DIAS[n - 1] * 86400000;
};
export const descartarOferta = () => {
    try { localStorage.setItem(CLAVE_DESCARTE, JSON.stringify({ n: leerDescarte().n + 1, t: Date.now() })); } catch (_e) { /* sin localStorage */ }
};

// Clave publica (VAPID) en el formato que pide el navegador
export const claveEnBytes = (base64) => {
    const relleno = "=".repeat((4 - (base64.length % 4)) % 4);
    const crudo = window.atob((base64 + relleno).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...crudo].map((c) => c.charCodeAt(0)));
};

let claveCache = null;
export const claveServidor = async () => {
    if (claveCache) return claveCache;
    try {
        const { data } = await AxioBoleteria.get("api/v1/push/clave");
        claveCache = { habilitado: !!(data && data.habilitado && data.publicKey), publicKey: (data && data.publicKey) || null };
    } catch (_e) {
        return { habilitado: false, publicKey: null };
    }
    return claveCache;
};
export const reiniciarClaveCache = () => { claveCache = null; };

/**
 * Suscribe este navegador. pedirPermiso: true solo cuando el cliente toco "Sí, avisarme" (es el unico momento en que sale el
 * cuadro del navegador). Sin permiso ya concedido y sin pedirPermiso no hace nada. codigoEvento: concierto que miraba;
 * idCompra + cedula: para avisarle cuando sus boletos esten listos (el servidor verifica que la compra sea de esa cedula).
 */
export const suscribirPush = async ({ codigoEvento = "", idCompra = null, cedula = "", pedirPermiso = false, permitirPanel = false } = {}) => {
    try {
        if (!puedeOfrecerPush({ permitirPanel })) return { ok: false, motivo: "no_disponible" };
        const clave = await claveServidor();
        if (!clave.habilitado) return { ok: false, motivo: "servidor_apagado" };
        if (Notification.permission === "default") {
            if (!pedirPermiso) return { ok: false, motivo: "sin_permiso" };
            const permiso = await Notification.requestPermission();
            if (permiso !== "granted") return { ok: false, motivo: "rechazado" };
        }
        const registro = await navigator.serviceWorker.register(RUTA_SW);
        await navigator.serviceWorker.ready;
        const suscripcion = (await registro.pushManager.getSubscription())
            || (await registro.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: claveEnBytes(clave.publicKey) }));
        const { data } = await AxioBoleteria.post("api/v1/push/suscribir", {
            subscription: suscripcion.toJSON(),
            ...(codigoEvento ? { codigoEvento: String(codigoEvento) } : {}),
            ...(idCompra && cedula ? { idCompra: Number(idCompra), cedula: String(cedula) } : {}),
        });
        return { ok: !!(data && data.success), vinculadaCompra: !!(data && data.vinculadaCompra), endpoint: suscripcion.endpoint };
    } catch (_e) {
        return { ok: false, motivo: "error" };
    }
};

export const darseDeBajaPush = async () => {
    try {
        const registro = await navigator.serviceWorker.getRegistration(RUTA_SW);
        const suscripcion = registro && (await registro.pushManager.getSubscription());
        if (!suscripcion) return { ok: true };
        await AxioBoleteria.post("api/v1/push/baja", { endpoint: suscripcion.endpoint });
        await suscripcion.unsubscribe();
        return { ok: true };
    } catch (_e) {
        return { ok: false };
    }
};

// Evento global: cualquier parte de la pagina avisa "buen momento para ofrecer los avisos" sin conocer al componente
export const EVENTO_OFRECER = "push:ofrecer";
export const ofrecerPush = (detalle = {}) => {
    try { window.dispatchEvent(new CustomEvent(EVENTO_OFRECER, { detail: detalle })); } catch (_e) { /* sin window */ }
};
